import { useEffect, useId, useState } from 'react';
import { AtSign, Loader, Search, X } from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import { useDebounce } from '../../hooks/useDebounce';
import type { DiscordGuildMember, PartnerTier } from '../../types/partners';
import { PartnerAvatar } from './PartnerAvatar';
import { discordName, parseDiscordId } from '../../utils/partnerLinks';
import { tierLabel } from '../../utils/partnerStatus';
import { cn } from '../../utils/cn';

/** Someone picked to join a partner team. Only `id` is certain; the rest comes from the bot when it can see them. */
export interface PickedDiscordMember {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  in_guild: boolean | null;
  partner_roles: PartnerTier[];
}

export const pickedFromId = (id: string): PickedDiscordMember => ({
  id,
  username: null,
  display_name: null,
  avatar_url: null,
  in_guild: null,
  partner_roles: [],
});

export const pickedFromMember = (member: DiscordGuildMember): PickedDiscordMember => ({
  id: member.id,
  username: member.username,
  display_name: member.display_name,
  avatar_url: member.avatar_url,
  in_guild: member.in_guild,
  partner_roles: member.partner_roles ?? [],
});

interface DiscordMemberPickerProps {
  value: PickedDiscordMember | null;
  onChange: (member: PickedDiscordMember | null) => void;
  /** Discord IDs already on this team: they cannot be picked twice. */
  takenIds?: ReadonlySet<string>;
  disabled?: boolean;
  label?: string;
}

interface Option {
  key: string;
  member: PickedDiscordMember;
  hints: string[];
  taken: boolean;
}

const roleHints = (roles: PartnerTier[] | undefined) => (roles ?? []).map((role) => `Already has the ${tierLabel(role).label} role`);

/**
 * Choose a Discord member for a partner team: search the server through
 * Mr. Roboto (bots are left out), or paste their Discord ID, an @mention or a
 * profile link when search cannot find them.
 */
export const DiscordMemberPicker = ({ value, onChange, takenIds, disabled = false, label = 'Discord member' }: DiscordMemberPickerProps) => {
  const inputId = useId();
  const listId = `${inputId}-list`;
  const [text, setText] = useState('');
  const debounced = useDebounce(text, 300);
  const [results, setResults] = useState<DiscordGuildMember[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    const q = debounced.trim();
    if (q.length < 2 || value) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    setSearchError(null);
    partnersApi
      .discordMembers({ q }, controller.signal)
      .then((members) => {
        setResults(members.filter((m) => !m.is_bot));
        setHighlight(0);
      })
      .catch((error) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setResults([]);
        setSearchError(error instanceof Error ? error.message : 'Search is unavailable.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setSearching(false);
      });
    return () => controller.abort();
  }, [debounced, value]);

  const pastedId = parseDiscordId(text);
  const options: Option[] = [];
  if (pastedId && !results.some((r) => r.id === pastedId)) {
    options.push({
      key: `id:${pastedId}`,
      member: pickedFromId(pastedId),
      hints: ['Use this Discord ID. Their name appears once Mr. Roboto sees them in the server.'],
      taken: !!takenIds?.has(pastedId),
    });
  }
  for (const member of results) {
    const hints = [
      ...roleHints(member.partner_roles),
      ...(member.in_guild ? [] : ['Not in the server: access applies once they join']),
      ...(member.is_staff ? ['Staff member'] : []),
    ];
    options.push({ key: member.id, member: pickedFromMember(member), hints, taken: !!takenIds?.has(member.id) });
  }

  const choose = (option: Option | undefined) => {
    if (!option || option.taken) return;
    onChange(option.member);
    setText('');
    setResults([]);
  };

  if (value) {
    const name = discordName(value, value.id);
    return (
      <div>
        <span className="block text-xs font-medium text-glass-400 mb-1.5">{label}</span>
        <div className="subcard p-2.5 flex items-center gap-3">
          <PartnerAvatar avatarUrl={value.avatar_url} name={name} size="md" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-white truncate">{name === value.id ? 'Discord member' : name}</p>
            <p className="text-[11px] font-mono text-glass-500 truncate">
              {value.username ? `@${value.username} · ` : ''}
              {value.id}
            </p>
            {!value.username && !value.display_name && (
              <p className="text-xs text-glass-500 mt-0.5">Their name appears here once Mr. Roboto sees them in the server.</p>
            )}
            {roleHints(value.partner_roles).map((hint) => (
              <p key={hint} className="text-xs text-glass-400 mt-0.5">{hint}. Adding them here keeps it, and makes it Argus’s to manage.</p>
            ))}
            {value.in_guild === false && <p className="text-xs text-amber-300 mt-0.5">They are not in the Discord server yet. Access and roles apply once they join.</p>}
          </div>
          {!disabled && (
            <button type="button" className="btn-icon btn-icon-sm" onClick={() => onChange(null)} title="Choose someone else" aria-label="Choose someone else">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  const showList = text.trim().length > 0 && (options.length > 0 || searching || !!searchError || debounced.trim().length >= 2);
  const invalidPaste = /^\d+$/.test(text.trim()) && !pastedId;

  return (
    <div>
      <label htmlFor={inputId} className="block text-xs font-medium text-glass-400 mb-1.5">
        {label}
      </label>
      <div className="relative">
        {/^\d/.test(text.trim()) ? (
          <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
        )}
        <input
          id={inputId}
          className="glass-input w-full pl-9 pr-9"
          autoComplete="off"
          placeholder="Search by name or @username, or paste a Discord ID"
          value={text}
          disabled={disabled}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          onChange={(e) => {
            setText(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={(e) => {
            if (!options.length) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((i) => Math.min(options.length - 1, i + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((i) => Math.max(0, i - 1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              choose(options[highlight]);
            } else if (e.key === 'Escape') {
              setText('');
            }
          }}
        />
        {searching && <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-glass-400" />}
      </div>

      {showList && (
        <ul id={listId} role="listbox" aria-label="Discord members" className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-glass-200/15 divide-y divide-glass-200/10">
          {options.map((option, index) => {
            const name = discordName(option.member, option.member.id);
            return (
              <li key={option.key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === highlight}
                  aria-disabled={option.taken}
                  disabled={option.taken}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => choose(option)}
                  className={cn(
                    'w-full text-left px-3 py-2 flex items-center gap-3 transition-colors touch-manipulation',
                    option.taken ? 'opacity-60 cursor-not-allowed' : index === highlight ? 'bg-accent-500/15' : 'hover:bg-glass-200/10',
                  )}
                >
                  <PartnerAvatar avatarUrl={option.member.avatar_url} name={name} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-white truncate">{name === option.member.id ? option.member.id : name}</span>
                    <span className="block text-[11px] font-mono text-glass-500 truncate">
                      {option.member.username ? `@${option.member.username}` : option.member.id}
                    </span>
                    {(option.taken ? ['Already on this team'] : option.hints).map((hint) => (
                      <span key={hint} className="block text-[11px] text-glass-400">{hint}</span>
                    ))}
                  </span>
                </button>
              </li>
            );
          })}
          {!searching && !searchError && options.length === 0 && debounced.trim().length >= 2 && (
            <li className="px-3 py-3 text-xs text-glass-500">
              Nobody in the server matches. If they are not in the server yet, paste their Discord ID instead.
            </li>
          )}
          {searchError && (
            <li className="px-3 py-3 text-xs text-amber-300">
              {searchError} You can still paste their Discord ID.
            </li>
          )}
        </ul>
      )}
      <p className={cn('text-xs mt-1', invalidPaste ? 'text-red-400' : 'text-glass-500')}>
        {invalidPaste
          ? 'A Discord ID is 17 to 20 digits. In Discord, turn on Developer Mode, then right-click the person and choose Copy User ID.'
          : 'Search the Discord server, or paste a Discord ID, @mention or profile link.'}
      </p>
    </div>
  );
};
